import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";
import { Pagination } from "../components/Pagination";
import Select from "./SelectScenarios";

const VALID_STATUSES = ["active", "inactive"];
const VALID_REGISTERS = ["active", "inactive"];
const VALID_TYPES = ["Mini", "Normal"];

const STATUS_LABELS = {
  active: "Active",
  inactive: "Inactive",
};

const REGISTER_LABELS = {
  active: "Active",
  inactive: "Inactive",
};

const STATUS_OPTIONS = VALID_STATUSES.map((status) => ({
  value: status,
  label: STATUS_LABELS[status],
}));

const REGISTER_OPTIONS = VALID_REGISTERS.map((reg) => ({
  value: reg,
  label: REGISTER_LABELS[reg],
}));

const TYPE_OPTIONS = VALID_TYPES.map((type) => ({
  value: type,
  label: type,
}));

export default function ScenathonList({ standalone = true }) {
  const { authFetch } = useAuth();

  const [scenathons, setSceenathons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [updating, setUpdating] = useState(null);

  const [search, setSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("id");
  const [sortDirection, setSortDirection] = useState("desc");

  const [page, setPage] = useState(1);
  const [perPage] = useState(10);

  // Default pathways
  const [expandedScenathonId, setExpandedScenathonId] = useState(null);
  const [defaultPathwaysByScenathon, setDefaultPathwaysByScenathon] = useState(
    {},
  );
  const [defaultPathwaysLoading, setDefaultPathwaysLoading] = useState({});
  const [defaultPathwaysSaving, setDefaultPathwaysSaving] = useState({});
  const [newDefaultPathwayByScenathon, setNewDefaultPathwayByScenathon] =
    useState({});
  const [editingDefaultPathways, setEditingDefaultPathways] = useState({});
  const [defaultPathwayDrafts, setDefaultPathwayDrafts] = useState({});

  const extractErrorMessage = async (response, fallback) => {
    try {
      const text = await response.text();
      if (!text) return fallback;

      try {
        const parsed = JSON.parse(text);
        return parsed?.error || parsed?.message || fallback;
      } catch {
        if (text.startsWith("<!DOCTYPE")) {
          return fallback;
        }
        return text;
      }
    } catch {
      return fallback;
    }
  };

  useEffect(() => {
    const loadSceenathons = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await authFetch("/api/scenathon/admin/all");

        if (!response.ok) {
          const errorMsg = await extractErrorMessage(
            response,
            "Error loading scenathons",
          );
          throw new Error(errorMsg);
        }

        const data = await response.json();
        setSceenathons(data.scenathons || []);
      } catch (err) {
        console.error("Load scenathons error:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadSceenathons();
  }, [authFetch]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPage(1);
  };

  const filtered = useMemo(() => {
    let result = scenathons;

    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(
        (s) =>
          (s.name || "").toLowerCase().includes(term) ||
          (s.notification_email || "").toLowerCase().includes(term) ||
          (s.type || "").toLowerCase().includes(term),
      );
    }

    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let av = a[sortColumn];
        let bv = b[sortColumn];

        if (av === null || av === undefined) av = "";
        if (bv === null || bv === undefined) bv = "";

        const avStr = av.toString().toLowerCase();
        const bvStr = bv.toString().toLowerCase();
        const dir = sortDirection === "asc" ? 1 : -1;

        if (avStr < bvStr) return -1 * dir;
        if (avStr > bvStr) return 1 * dir;
        return 0;
      });
    }

    return result;
  }, [scenathons, search, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const pageData = filtered.slice((page - 1) * perPage, page * perPage);

  const handleStartEdit = (scenathon) => {
    setEditingId(scenathon.id);
    setEditData({
      name: scenathon.name,
      notification_email: scenathon.notification_email || "",
      status: scenathon.status,
      allow_trade: scenathon.allow_trade,
      year: scenathon.year || "",
      register: scenathon.register,
      type: scenathon.type || "",
    });
  };

  const handleSaveChanges = async (scenathon) => {
    try {
      setUpdating(scenathon.id);

      const response = await authFetch(
        `/api/scenathon/scenathons/${scenathon.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editData),
        },
      );

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(
          response,
          "Error updating scenathon",
        );
        throw new Error(errorMsg);
      }

      const data = await response.json();

      setSceenathons((prev) =>
        prev.map((s) => (s.id === scenathon.id ? data.scenathon : s)),
      );

      setEditingId(null);

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Scenathon updated successfully",
      });
    } catch (err) {
      console.error("Update error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setUpdating(null);
    }
  };

  const loadDefaultPathways = async (scenathonId) => {
    try {
      setDefaultPathwaysLoading((prev) => ({ ...prev, [scenathonId]: true }));

      const response = await authFetch(
        `/api/scenathon/default-pathways/${scenathonId}`,
      );

      // Si no existe la ruta o no hay registros, no bloquear la UI
      if (response.status === 404) {
        setDefaultPathwaysByScenathon((prev) => ({
          ...prev,
          [scenathonId]: [],
        }));

        setNewDefaultPathwayByScenathon((prev) => ({
          ...prev,
          [scenathonId]: {
            defaultname: "",
            deadline: "",
          },
        }));
        return;
      }

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(
          response,
          "Error loading default pathways",
        );
        throw new Error(errorMsg);
      }

      const data = await response.json();

      setDefaultPathwaysByScenathon((prev) => ({
        ...prev,
        [scenathonId]: Array.isArray(data.defaultPathways)
          ? data.defaultPathways
          : [],
      }));

      setNewDefaultPathwayByScenathon((prev) => ({
        ...prev,
        [scenathonId]: {
          defaultname: "",
          deadline: "",
        },
      }));
    } catch (err) {
      console.error("Load default pathways error:", err);

      // Fallback silencioso: abrir la sección aunque no haya datos
      setDefaultPathwaysByScenathon((prev) => ({
        ...prev,
        [scenathonId]: [],
      }));

      setNewDefaultPathwayByScenathon((prev) => ({
        ...prev,
        [scenathonId]: {
          defaultname: "",
          deadline: "",
        },
      }));
    } finally {
      setDefaultPathwaysLoading((prev) => ({ ...prev, [scenathonId]: false }));
    }
  };

  const handleToggleDefaultPathways = async (scenathonId) => {
    if (expandedScenathonId === scenathonId) {
      setExpandedScenathonId(null);
      return;
    }

    setExpandedScenathonId(scenathonId);

    if (!defaultPathwaysByScenathon[scenathonId]) {
      await loadDefaultPathways(scenathonId);
    }
  };

  const handleStartEditDefaultPathway = (scenathonId, pathway) => {
    const key = `${scenathonId}-${pathway.id}`;

    setEditingDefaultPathways((prev) => ({
      ...prev,
      [key]: true,
    }));

    setDefaultPathwayDrafts((prev) => ({
      ...prev,
      [key]: {
        defaultname: pathway.defaultname || "",
        deadline: pathway.deadline || "",
      },
    }));
  };

  const handleDraftDefaultPathwayChange = (
    scenathonId,
    pathwayId,
    field,
    value,
  ) => {
    const key = `${scenathonId}-${pathwayId}`;

    setDefaultPathwayDrafts((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),
        [field]: value,
      },
    }));
  };

  const handleSaveDefaultPathway = async (scenathonId, pathway) => {
    const key = `${scenathonId}-${pathway.id}`;
    const draft = defaultPathwayDrafts[key] || {
      defaultname: pathway.defaultname || "",
      deadline: pathway.deadline || "",
    };

    if (!draft.defaultname?.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Default pathway name is required",
      });
      return;
    }

    if (!draft.deadline) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Deadline is required",
      });
      return;
    }

    try {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [key]: true }));

      const response = await authFetch(
        `/api/scenathon/default-pathways/${pathway.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            defaultname: draft.defaultname.trim(),
            deadline: draft.deadline,
          }),
        },
      );

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(
          response,
          "Error updating default pathway",
        );
        throw new Error(errorMsg);
      }

      const data = await response.json();

      setDefaultPathwaysByScenathon((prev) => ({
        ...prev,
        [scenathonId]: (prev[scenathonId] || []).map((item) =>
          item.id === pathway.id ? data.defaultPathway : item,
        ),
      }));

      setEditingDefaultPathways((prev) => ({
        ...prev,
        [key]: false,
      }));

      setDefaultPathwayDrafts((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Default pathway updated successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Update default pathway error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAddDefaultPathway = async (scenathonId) => {
    const newItem = newDefaultPathwayByScenathon[scenathonId] || {
      defaultname: "",
      deadline: "",
    };

    if (!newItem.defaultname?.trim()) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Default pathway name is required",
      });
      return;
    }

    if (!newItem.deadline) {
      await Swal.fire({
        icon: "warning",
        title: "Validation",
        text: "Deadline is required",
      });
      return;
    }

    const saveKey = `new-${scenathonId}`;

    try {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [saveKey]: true }));

      const response = await authFetch(`/api/scenathon/default-pathways`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          scenathon_id: scenathonId,
          defaultname: newItem.defaultname.trim(),
          deadline: newItem.deadline,
        }),
      });

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(
          response,
          "Route /api/scenathon/default-pathways not found or not returning JSON",
        );
        throw new Error(errorMsg);
      }

      const data = await response.json();

      setDefaultPathwaysByScenathon((prev) => ({
        ...prev,
        [scenathonId]: [...(prev[scenathonId] || []), data.defaultPathway],
      }));

      setNewDefaultPathwayByScenathon((prev) => ({
        ...prev,
        [scenathonId]: {
          defaultname: "",
          deadline: "",
        },
      }));

      await Swal.fire({
        icon: "success",
        title: "Success",
        text: "Default pathway created successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Create default pathway error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [saveKey]: false }));
    }
  };

  const handleDeleteDefaultPathway = async (scenathonId, pathwayId) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Delete default pathway?",
      text: "This action cannot be undone.",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
    });

    if (!confirm.isConfirmed) return;

    const deleteKey = `delete-${scenathonId}-${pathwayId}`;

    try {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [deleteKey]: true }));

      const response = await authFetch(
        `/api/scenathon/default-pathways/${pathwayId}`,
        {
          method: "DELETE",
        },
      );

      if (!response.ok) {
        const errorMsg = await extractErrorMessage(
          response,
          "Error deleting default pathway",
        );
        throw new Error(errorMsg);
      }

      setDefaultPathwaysByScenathon((prev) => ({
        ...prev,
        [scenathonId]: (prev[scenathonId] || []).filter(
          (item) => item.id !== pathwayId,
        ),
      }));

      setEditingDefaultPathways((prev) => {
        const next = { ...prev };
        delete next[`${scenathonId}-${pathwayId}`];
        return next;
      });

      setDefaultPathwayDrafts((prev) => {
        const next = { ...prev };
        delete next[`${scenathonId}-${pathwayId}`];
        return next;
      });

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Default pathway deleted successfully",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (err) {
      console.error("Delete default pathway error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err.message,
      });
    } finally {
      setDefaultPathwaysSaving((prev) => ({ ...prev, [deleteKey]: false }));
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading scenathons...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">⚠️ Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`w-full p-4 md:p-6 font-montserrat space-y-6 ${
        standalone ? "max-w-7xl mx-auto" : ""
      }`}
    >
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Scenathons</h2>
        <p className="text-gray-600">
          View and edit existing scenathons ({scenathons.length} total)
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-sm p-4">
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search by name, email, or type..."
        />
      </div>

      <div className="bg-white rounded-lg shadow-sm">
        <div className="overflow-x-auto" style={{ overflowY: "visible" }}>
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th
                  onClick={() => handleSort("id")}
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  ID{" "}
                  {sortColumn === "id" && (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  onClick={() => handleSort("name")}
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  Name{" "}
                  {sortColumn === "name" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  onClick={() => handleSort("year")}
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  Year{" "}
                  {sortColumn === "year" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th
                  onClick={() => handleSort("type")}
                  className="px-6 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                >
                  Type{" "}
                  {sortColumn === "type" &&
                    (sortDirection === "asc" ? "↑" : "↓")}
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Register
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Trade
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {pageData.map((scenathon) => {
                const isExpanded = expandedScenathonId === scenathon.id;
                const defaultPathways =
                  defaultPathwaysByScenathon[scenathon.id] || [];
                const newDefaultPathway = newDefaultPathwayByScenathon[
                  scenathon.id
                ] || {
                  defaultname: "",
                  deadline: "",
                };

                return (
                  <React.Fragment key={scenathon.id}>
                    <tr className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                        {scenathon.id}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {editingId === scenathon.id ? (
                          <input
                            type="text"
                            value={editData.name}
                            onChange={(e) =>
                              setEditData({ ...editData, name: e.target.value })
                            }
                            disabled={updating === scenathon.id}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors disabled:bg-gray-100 disabled:opacity-60"
                          />
                        ) : (
                          scenathon.name
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {editingId === scenathon.id ? (
                          <input
                            type="number"
                            value={editData.year}
                            onChange={(e) =>
                              setEditData({ ...editData, year: e.target.value })
                            }
                            disabled={updating === scenathon.id}
                            className="w-full px-3 py-2 border-2 border-gray-300 rounded-lg text-sm bg-white placeholder-gray-400 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-200 transition-colors disabled:bg-gray-100 disabled:opacity-60"
                          />
                        ) : (
                          scenathon.year || "-"
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm text-gray-600">
                        {editingId === scenathon.id ? (
                          <Select
                            value={editData.type}
                            onChange={(value) =>
                              setEditData({ ...editData, type: value })
                            }
                            options={TYPE_OPTIONS}
                            disabled={updating === scenathon.id}
                            placeholder="Select type"
                          />
                        ) : (
                          scenathon.type || "-"
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {editingId === scenathon.id ? (
                          <Select
                            value={editData.status}
                            onChange={(value) =>
                              setEditData({ ...editData, status: value })
                            }
                            options={STATUS_OPTIONS}
                            disabled={updating === scenathon.id}
                            placeholder="Select status"
                          />
                        ) : (
                          <span className="inline-flex px-3 py-1 bg-gray-100 text-gray-800 rounded-full text-sm font-medium">
                            {STATUS_LABELS[scenathon.status] ||
                              scenathon.status}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {editingId === scenathon.id ? (
                          <Select
                            value={editData.register}
                            onChange={(value) =>
                              setEditData({ ...editData, register: value })
                            }
                            options={REGISTER_OPTIONS}
                            disabled={updating === scenathon.id}
                            placeholder="Select register"
                          />
                        ) : (
                          <span className="inline-flex px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                            {REGISTER_LABELS[scenathon.register] ||
                              scenathon.register}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {editingId === scenathon.id ? (
                          <input
                            type="checkbox"
                            checked={editData.allow_trade}
                            onChange={(e) =>
                              setEditData({
                                ...editData,
                                allow_trade: e.target.checked,
                              })
                            }
                            disabled={updating === scenathon.id}
                            className="w-4 h-4"
                          />
                        ) : (
                          <span
                            className={
                              scenathon.allow_trade
                                ? "text-green-600 font-bold"
                                : "text-gray-400"
                            }
                          >
                            {scenathon.allow_trade ? "✓" : "✗"}
                          </span>
                        )}
                      </td>

                      <td className="px-6 py-4 text-sm">
                        {editingId === scenathon.id ? (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveChanges(scenathon)}
                              disabled={updating === scenathon.id}
                              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium transition-colors"
                            >
                              {updating === scenathon.id ? "Saving..." : "Save"}
                            </button>
                            <button
                              onClick={() => setEditingId(null)}
                              disabled={updating === scenathon.id}
                              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 text-sm font-medium transition-colors"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            <button
                              onClick={() => handleStartEdit(scenathon)}
                              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() =>
                                handleToggleDefaultPathways(scenathon.id)
                              }
                              className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-800 text-sm font-medium transition-colors"
                            >
                              {isExpanded
                                ? "Hide Default Pathways"
                                : "Default Pathways"}
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>

                    {isExpanded && (
                      <tr className="bg-slate-50">
                        <td colSpan={8} className="px-6 py-6">
                          <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-5">
                            <div>
                              <h3 className="text-lg font-semibold text-slate-900">
                                Default Pathways - {scenathon.name}
                              </h3>
                              <p className="text-sm text-slate-600">
                                Manage the default pathway names and deadlines
                                for this scenathon.
                              </p>
                            </div>

                            {defaultPathwaysLoading[scenathon.id] ? (
                              <div className="rounded-lg border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
                                Loading default pathways...
                              </div>
                            ) : (
                              <>
                                <div className="overflow-x-auto">
                                  <table className="w-full text-sm">
                                    <thead className="bg-slate-50 border-b border-slate-200">
                                      <tr>
                                        <th className="px-4 py-3 text-left font-semibold text-slate-700">
                                          ID
                                        </th>
                                        <th className="px-4 py-3 text-left font-semibold text-slate-700">
                                          Default Pathway Name
                                        </th>
                                        <th className="px-4 py-3 text-left font-semibold text-slate-700">
                                          Deadline
                                        </th>
                                        <th className="px-4 py-3 text-left font-semibold text-slate-700">
                                          Actions
                                        </th>
                                      </tr>
                                    </thead>

                                    <tbody className="divide-y divide-slate-200">
                                      {defaultPathways.map((pathway) => {
                                        const key = `${scenathon.id}-${pathway.id}`;
                                        const deleteKey = `delete-${scenathon.id}-${pathway.id}`;
                                        const isEditing =
                                          !!editingDefaultPathways[key];
                                        const draft = defaultPathwayDrafts[
                                          key
                                        ] || {
                                          defaultname:
                                            pathway.defaultname || "",
                                          deadline: pathway.deadline || "",
                                        };

                                        return (
                                          <tr key={pathway.id}>
                                            <td className="px-4 py-3 text-slate-700">
                                              {pathway.id}
                                            </td>

                                            <td className="px-4 py-3">
                                              {isEditing ? (
                                                <input
                                                  type="text"
                                                  value={draft.defaultname}
                                                  onChange={(e) =>
                                                    handleDraftDefaultPathwayChange(
                                                      scenathon.id,
                                                      pathway.id,
                                                      "defaultname",
                                                      e.target.value,
                                                    )
                                                  }
                                                  disabled={
                                                    defaultPathwaysSaving[
                                                      key
                                                    ] ||
                                                    defaultPathwaysSaving[
                                                      deleteKey
                                                    ]
                                                  }
                                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                                />
                                              ) : (
                                                <span className="text-slate-700">
                                                  {pathway.defaultname}
                                                </span>
                                              )}
                                            </td>

                                            <td className="px-4 py-3">
                                              {isEditing ? (
                                                <input
                                                  type="date"
                                                  value={draft.deadline || ""}
                                                  onChange={(e) =>
                                                    handleDraftDefaultPathwayChange(
                                                      scenathon.id,
                                                      pathway.id,
                                                      "deadline",
                                                      e.target.value,
                                                    )
                                                  }
                                                  disabled={
                                                    defaultPathwaysSaving[
                                                      key
                                                    ] ||
                                                    defaultPathwaysSaving[
                                                      deleteKey
                                                    ]
                                                  }
                                                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                                />
                                              ) : (
                                                <span className="text-slate-700">
                                                  {pathway.deadline
                                                    ? new Date(
                                                        pathway.deadline,
                                                      ).toLocaleDateString()
                                                    : "-"}
                                                </span>
                                              )}
                                            </td>

                                            <td className="px-4 py-3">
                                              <div className="flex flex-wrap gap-2">
                                                {isEditing ? (
                                                  <>
                                                    <button
                                                      onClick={() =>
                                                        handleSaveDefaultPathway(
                                                          scenathon.id,
                                                          pathway,
                                                        )
                                                      }
                                                      disabled={
                                                        defaultPathwaysSaving[
                                                          key
                                                        ] ||
                                                        defaultPathwaysSaving[
                                                          deleteKey
                                                        ]
                                                      }
                                                      className="rounded-lg bg-green-600 px-3 py-2 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                                                    >
                                                      {defaultPathwaysSaving[
                                                        key
                                                      ]
                                                        ? "Saving..."
                                                        : "Save"}
                                                    </button>

                                                    <button
                                                      onClick={() => {
                                                        setEditingDefaultPathways(
                                                          (prev) => ({
                                                            ...prev,
                                                            [key]: false,
                                                          }),
                                                        );

                                                        setDefaultPathwayDrafts(
                                                          (prev) => {
                                                            const next = {
                                                              ...prev,
                                                            };
                                                            delete next[key];
                                                            return next;
                                                          },
                                                        );
                                                      }}
                                                      disabled={
                                                        defaultPathwaysSaving[
                                                          key
                                                        ] ||
                                                        defaultPathwaysSaving[
                                                          deleteKey
                                                        ]
                                                      }
                                                      className="rounded-lg bg-gray-500 px-3 py-2 text-xs font-medium text-white hover:bg-gray-600 disabled:opacity-50"
                                                    >
                                                      Cancel
                                                    </button>
                                                  </>
                                                ) : (
                                                  <>
                                                    <button
                                                      onClick={() =>
                                                        handleStartEditDefaultPathway(
                                                          scenathon.id,
                                                          pathway,
                                                        )
                                                      }
                                                      disabled={
                                                        defaultPathwaysSaving[
                                                          key
                                                        ] ||
                                                        defaultPathwaysSaving[
                                                          deleteKey
                                                        ]
                                                      }
                                                      className="rounded-lg bg-blue-600 px-3 py-2 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                                    >
                                                      Edit
                                                    </button>

                                                    <button
                                                      onClick={() =>
                                                        handleDeleteDefaultPathway(
                                                          scenathon.id,
                                                          pathway.id,
                                                        )
                                                      }
                                                      disabled={
                                                        defaultPathwaysSaving[
                                                          key
                                                        ] ||
                                                        defaultPathwaysSaving[
                                                          deleteKey
                                                        ]
                                                      }
                                                      className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
                                                    >
                                                      {defaultPathwaysSaving[
                                                        deleteKey
                                                      ]
                                                        ? "Deleting..."
                                                        : "Delete"}
                                                    </button>
                                                  </>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        );
                                      })}
                                    </tbody>
                                  </table>
                                </div>

                                {defaultPathways.length === 0 && (
                                  <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-6 text-center text-sm text-slate-500">
                                    No default pathways found for this
                                    scenathon.
                                  </div>
                                )}

                                <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                                  <h4 className="mb-3 text-sm font-semibold text-slate-800">
                                    Add new default pathway
                                  </h4>

                                  <div className="grid gap-3 md:grid-cols-3">
                                    <input
                                      type="text"
                                      value={
                                        newDefaultPathway.defaultname || ""
                                      }
                                      onChange={(e) =>
                                        setNewDefaultPathwayByScenathon(
                                          (prev) => ({
                                            ...prev,
                                            [scenathon.id]: {
                                              ...newDefaultPathway,
                                              defaultname: e.target.value,
                                            },
                                          }),
                                        )
                                      }
                                      placeholder="Default pathway name"
                                      disabled={
                                        defaultPathwaysSaving[
                                          `new-${scenathon.id}`
                                        ]
                                      }
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                    />

                                    <input
                                      type="date"
                                      value={newDefaultPathway.deadline || ""}
                                      onChange={(e) =>
                                        setNewDefaultPathwayByScenathon(
                                          (prev) => ({
                                            ...prev,
                                            [scenathon.id]: {
                                              ...newDefaultPathway,
                                              deadline: e.target.value,
                                            },
                                          }),
                                        )
                                      }
                                      disabled={
                                        defaultPathwaysSaving[
                                          `new-${scenathon.id}`
                                        ]
                                      }
                                      className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-200"
                                    />

                                    <button
                                      onClick={() =>
                                        handleAddDefaultPathway(scenathon.id)
                                      }
                                      disabled={
                                        defaultPathwaysSaving[
                                          `new-${scenathon.id}`
                                        ]
                                      }
                                      className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                                    >
                                      {defaultPathwaysSaving[
                                        `new-${scenathon.id}`
                                      ]
                                        ? "Adding..."
                                        : "Add default pathway"}
                                    </button>
                                  </div>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        {pageData.length === 0 && (
          <div className="px-6 py-12 text-center text-gray-500">
            <p className="text-lg">No scenathons to display</p>
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm p-4">
          <Pagination
            currentPage={page}
            totalPages={totalPages}
            onPageChange={setPage}
          />
        </div>
      )}

      <div className="text-sm text-gray-600 text-center bg-blue-50 rounded-lg p-4">
        <p>
          Showing <strong>{pageData.length}</strong> of{" "}
          <strong>{filtered.length}</strong> scenathon(s)
          {search.trim() && ` (filtered from ${scenathons.length} total)`}
        </p>
      </div>
    </div>
  );
}
